import Service from '@ember/service';
import { computed } from '@ember/object';
import { service } from '@ember/service';
import { waitForPromise } from '@ember/test-waiters';
import cloneDeep from 'lodash.clonedeep';

import Utils from '../utils/utils';
import Consts from '../utils/constants';

export default class PartDataService extends Service {
    @service intl;
    @service variantData;
    @service urlData;
    @service('aditools-site') siteconfig;

    load() {
        let configUrl = this.siteconfig.ConfigUrl + '?id=' + new Date().getTime();
        return waitForPromise(
            fetch(configUrl).then((response) => {
                // Load tool data from config file
                if (!response.ok)
                    console.error(`An error occurred while getting the tools config data: ${response.statusText}`);
                else {
                    return response.json().then((data) => {
                        this.set('data', data); // not optimal but will work until we can get a model set up
                        return data;
                    });
                }
            }),
        );
    }

    @computed('data.toolName') get toolName() {
        if (!this.data && this.data.toolName) return 'sardriver';
        else return this.data.toolName;
    }

    @computed('data.defaults') get defaults() {
        if (!(this.data && this.data.defaults)) return {};
        else return cloneDeep(this.data.defaults);
    }

    @computed('data.sigmaDeltaInputs') get sigmaDeltaInputs() {
        if (!(this.data && this.data.sigmaDeltaInputs)) return {};
        else return cloneDeep(this.data.sigmaDeltaInputs);
    }

    @computed('data.adc') get adcs() {
        if (!this.data) return [];

        let i = 0;
        return Object.keys(this.data.adc).map((key) => this._mapAdc(++i, key));
    }

    @computed('data.driver') get drivers() {
        if (!this.data) return [];

        let i = 0;
        return Object.keys(this.data.driver).map((key) => this._mapDriver(++i, key));
    }

    @computed('data.mux') get muxParts() {
        if (!this.data) return [];

        let i = 0;
        return Object.keys(this.data.mux).map((key) => this._mapMux(++i, key));
    }

    _mapMux(id, key) {
        let mux = cloneDeep(this.data.mux[key]);
        mux.id = id;
        mux.part = key;
        mux.partCore = key;
        mux.datasheet = this.urlData.getDatasheetUrl(mux);

        return mux;
    }

    _mapDriver(id, key) {
        let driver = cloneDeep(this.data.driver[key]),
            intl = this.intl;

        driver.id = id;
        driver.part = key;
        driver.slewRateVus = Utils.roundTo(driver.slewRate / 1000000, 0);
        driver.IoutLimit = driver.IoutLinear || driver.IoutSC * 0.8;
        driver.basicToolNeeds = true;
        driver.timeDomainNeeds = true;
        driver.noiseSimulationNeeds = true;
        driver.thdSimulationNeeds = true;
        driver.invalidProperties = [];

        driver.selectable =
            (typeof driver.selectable == 'undefined' || driver.selectable) &&
            !this.variantData.currentItemHasVariant(driver.part);

        // Get formatted version of type (if this is a computed cell, it is not filtered correctly)
        let typeFormatted = '';
        switch (driver.type.toLowerCase()) {
            case 'amp':
                typeFormatted = intl.t('driver_types.amp').toString();
                break;
            case 'diffamp':
                typeFormatted = intl.t('driver_types.diffamp').toString();
                break;
        }
        driver.driverTypeFormatted = typeFormatted;

        this.variantData.generateVariantProperties(driver, this.data.driver, 'driver');
        driver.datasheet = this.urlData.getDatasheetUrl(driver);

        return driver;
    }

    _mapAdc(id, key) {
        let adc = cloneDeep(this.data.adc[key]),
            intl = this.intl;

        adc.id = id;
        adc.part = key;

        adc.selectable =
            (typeof adc.selectable === 'undefined' || adc.selectable) &&
            !this.variantData.currentItemHasVariant(adc.part);
        adc.inLTSpice = typeof adc.inLTSpice != 'undefined' && adc.inLTSpice;
        adc.fftLTSpice = typeof adc.FFTLTspice != 'undefined' && adc.FFTLTspice;
        adc.schematicTemplate = adc.schematicTemplate ? adc.schematicTemplate : 'AD40xx';
        adc.typeDisplay =
            adc.type === Consts.ADC_TYPE.SE
                ? intl.t('adc_type_display.single_ended').toString()
                : intl.t('adc_type_display.differential').toString();

        adc.samplingRate = adc.maxSampleRate;
        adc.vref = adc.vrefTest;
        adc.rext = adc.datasheetRfilt;
        adc.cext = adc.datasheetCfilt;

        adc.invalidData = false;

        adc.adcSnrLinear = Math.pow(10, adc.ADCSNR / 20);
        adc.adcNoisePtoP = (1 / adc.adcSnrLinear) * (adc.type === Consts.ADC_TYPE.SE ? adc.vrefTest : 2 * adc.vrefTest);
        adc.adcNoiseRms = adc.adcNoisePtoP / (2 * Math.sqrt(2));

        adc.recommendedDrivers = this.data.driversForADC[key] ? this.data.driversForADC[key] : [];

        this.variantData.generateVariantProperties(adc, this.data.adc);
        adc.datasheet = this.urlData.getDatasheetUrl(adc);

        return adc;
    }
}
